import { UserRightsRoleModel } from "../../models/rights/user-rights-role/user-rights-role.model";
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { Injectable } from "@angular/core";
import { finalize, from, Observable, of, throwError } from "rxjs";
import { delayValueRx, RequestClientService } from "../../services/client/request-client.service";
import { map } from "rxjs/operators";
import { UserRightsRoleDetailAction } from './user-rights-role-detail.action';
import {
  UserRightsRoleToUserRightsFunctionDTO
} from "../../models/rights/user-rights-role/UserRightsRoleToUserRightsFunctionDTO";
import { ResetStateDefaults } from "../base-action-class.model";
import { UserRightsRoleRequestService } from '../../services/request/user-rights-role-request.service';
import { UserRightsFunctionRequestService } from '../../services/request/user-rights-function-request.service';
import { RUserRoleXsState } from '../user-rights/topic-based/user-role/r-user-role-xs.state';


interface UserRightsRoleDetailStateModel {
  id: string;
  userRightsRole: UserRightsRoleModel;
  selectedUserRightsFunctionIds: Set<string>;
  inProgress: boolean;
  deletable: boolean;
}

const getDefaults = (): UserRightsRoleDetailStateModel => ({
  id: null,
  userRightsRole: null,
  selectedUserRightsFunctionIds: new Set<string>(),
  inProgress: false,
  deletable: false,
});

@State<UserRightsRoleDetailStateModel>({
  name: 'userRightsRoleDetail',
  defaults: getDefaults()
})
@Injectable()
export class UserRightsRoleDetailXsState {

  constructor(
    private store: Store,
    private userRightsRoleRequestService: UserRightsRoleRequestService,
    private userRightsFunctionRequestService: UserRightsFunctionRequestService
  ) {
  }

  @Selector()
  static userRightsRole(state: UserRightsRoleDetailStateModel): UserRightsRoleModel | null {
    return state.userRightsRole || null;
  }

  @Selector()
  static userRightsRoleId(state: UserRightsRoleDetailStateModel): string | null {
    return state.id || null;
  }

  @Selector()
  static inProgress(state: UserRightsRoleDetailStateModel): boolean {
    return state.inProgress;
  }

  @Selector()
  static deletable(state: UserRightsRoleDetailStateModel): boolean {
    return state.deletable;
  }

  @Selector()
  static selectedRightsFunctionIds(state: UserRightsRoleDetailStateModel): Set<string> {
    return state.selectedUserRightsFunctionIds;
  }

  @Action(UserRightsRoleDetailAction.LoadUserRightsRole)
  public loadUser(
    ctx: StateContext<UserRightsRoleDetailStateModel>,
    action: UserRightsRoleDetailAction.LoadUserRightsRole
  ): Observable<void> {
    const state = ctx.getState();

    if (state.inProgress) {
      return throwError(() => new Error('User group request is in progress!'));
    }
    if (!action.id) {
      return throwError(() => new Error('User group id must be set!'));
    }
    if (state.id === action.id) {
      return of(null);
    }

    ctx.patchState({
      id: null,
      userRightsRole: null,
      selectedUserRightsFunctionIds: new Set()
    });

    const start = new Date();

    return from(
      this.userRightsRoleRequestService.getById(action.id)
    ).pipe(
      delayValueRx(start),
      map((userRightsRole: UserRightsRoleModel) => {
        ctx.patchState({
          id: userRightsRole.id,
          userRightsRole: userRightsRole,
          deletable: this.store.selectSnapshot(RUserRoleXsState.canDeleteUserRoleByModel(userRightsRole)),
          selectedUserRightsFunctionIds: new Set<string>(userRightsRole.usedRightsFunctionIds || [])
        });
      })
    );
  }

  @Action(UserRightsRoleDetailAction.UpdateBasicInfo)
  public updateBasicInfo(
    ctx: StateContext<UserRightsRoleDetailStateModel>,
    action: UserRightsRoleDetailAction.UpdateBasicInfo
  ): Observable<any> {
    const state = ctx.getState();

    if (state.inProgress) {
      return throwError(() => new Error('User request is in progress!'));
    }
    if (!action.basicInfo) {
      return throwError(() => new Error('Basic info must be set!'));
    }
    if (!action.basicInfo.nameCV) {
      return throwError(() => new Error('NameCV must be set!'));
    }

    ctx.patchState({
      inProgress: true
    });

    const model = UserRightsRoleModel.fromBasicInfo(state.id, action.basicInfo);

    return this.userRightsRoleRequestService.updateUserRole(model)
      .pipe(
        map((userRightsRole: any) => {
          ctx.patchState({userRightsRole: userRightsRole});
        }),
        finalize(() => {
          ctx.patchState({inProgress: false});
        })
      );
  }

  @Action(UserRightsRoleDetailAction.SelectRightsFunction)
  public selectRightsFunction(
    ctx: StateContext<UserRightsRoleDetailStateModel>,
    action: UserRightsRoleDetailAction.SelectRightsFunction
  ): Observable<void> {
    const state = ctx.getState();

    if (state.inProgress) {
      return throwError(() => new Error('User request is in progress!'));
    }
    if (!action.rightsRoleId) {
      return throwError(() => new Error('Rights function id must be set!'));
    }

    const dto = new UserRightsRoleToUserRightsFunctionDTO();
    dto.userRightsRoleId = state.id;
    dto.userRightsFunctionIds.push(action.rightsRoleId);

    return this.userRightsFunctionRequestService.addUserRightsFunctionToUserRightsRole(dto)
      .pipe(
        map(() => {
          const selectedRightsFunctionIds = new Set(state.selectedUserRightsFunctionIds);
          selectedRightsFunctionIds.add(action.rightsRoleId);
          ctx.patchState({selectedUserRightsFunctionIds: selectedRightsFunctionIds});
        }));
  }

  @Action(UserRightsRoleDetailAction.UnselectRightsFunction)
  public unselectRightsFunction(
    ctx: StateContext<UserRightsRoleDetailStateModel>,
    action: UserRightsRoleDetailAction.UnselectRightsFunction
  ): Observable<void> {
    const state = ctx.getState();

    if (state.inProgress) {
      return throwError(() => new Error('User request is in progress!'));
    }
    if (!action.rightsRoleId) {
      return throwError(() => new Error('Rights function id must be set!'));
    }

    const dto = new UserRightsRoleToUserRightsFunctionDTO();
    dto.userRightsRoleId = state.id;
    dto.userRightsFunctionIds.push(action.rightsRoleId);

    return this.userRightsFunctionRequestService.removeUserRightsFunctionFromUserRightsRole(dto)
      .pipe(
        map(() => {
          const selectedRightsFunctionIds = new Set(state.selectedUserRightsFunctionIds);
          selectedRightsFunctionIds.delete(action.rightsRoleId);
          ctx.patchState({selectedUserRightsFunctionIds: selectedRightsFunctionIds});
        })
      );
  }

  @Action(ResetStateDefaults)
  public resetDefaults(ctx: StateContext<UserRightsRoleDetailStateModel>): void {
    ctx.setState({...getDefaults()});
  }

  @Action(UserRightsRoleDetailAction.DeleteUserRightsRole)
  public delete(
    ctx: StateContext<UserRightsRoleDetailStateModel>,
    action: UserRightsRoleDetailAction.DeleteUserRightsRole
  ): Observable<void> {
    const state = ctx.getState();

    if (state.inProgress) {
      return throwError(() => new Error('User group request is in progress!'));
    }
    if (!state.userRightsRole) {
      return throwError(() => new Error('User group must be set!'));
    }

    const start = new Date();

    return from(
      this.userRightsRoleRequestService.deleteUserRole(state.userRightsRole.id)
    ).pipe(
      delayValueRx(start),
      map(() => {
        ctx.setState({...getDefaults()});
      })
    );
  }

}
